import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    useEffect,
} from 'react'
import ReactDOM from 'react-dom'
import { useSelector } from 'react-redux'
import './PlaydynamexNew.css'
import { useHistory } from 'react-router'
import ProgramHeaderTwo from '../ReusableComponents/Header/ProgramHeaderTwo'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import MakeContext from '../Make/MakeContext'
import ContentEngine from '../ContentEngine/ContentEngine'

import {
    MdOutlineArrowBack,
    MdOutlineViewInAr,
    MdFullscreen,
    MdClosedCaption,
    MdInfoOutline,
    MdUsb,
    MdCheckCircle,
} from 'react-icons/md'
import { BsStars } from 'react-icons/bs'
import { FaShapes, FaLock } from 'react-icons/fa6'
import { GrPrevious, GrNext } from 'react-icons/gr'
import { RxReset } from 'react-icons/rx'

import getBackendImage from '../../source/imageUtil'
import { parseLiveBytes } from '../PlayScreen/liveBytesParser'
import Toast from '../ReusableComponents/UI/Toast'
// CONSTANTS
const BACKEND_URL = process.env.REACT_APP_API_BASE
// const PROJECT_ID = queryProjectId || 'talking-dragon'

const orbitFromCameraData = (cam) => {
    if (!cam?.location) return null
    const { x, y, z } = cam.location
    const mx = x
    const my = z
    const mz = -y
    const radius = Math.sqrt(mx * mx + my * my + mz * mz)
    if (radius === 0) return null // degenerate — camera at origin
    const theta = Math.atan2(mx, mz) * (180 / Math.PI)
    const phi =
        Math.acos(Math.min(1, Math.max(-1, my / radius))) * (180 / Math.PI)
    return `${theta.toFixed(2)}deg ${phi.toFixed(2)}deg ${radius.toFixed(3)}m`
}

// Apply a step's cameraData to the model-viewer element.
// Prefers the web-native captured format (orbit/target/fieldOfView), which
// needs NO coordinate conversion — it's exactly what the viewer produced when
// the designer framed the shot in the preview. Falls back to the legacy
// Blender `location` (which goes through the axis-swap conversion above) so
// older projects keep working. Returns true if anything was applied.
const applyCameraToViewer = (mv, cam) => {
    if (!mv || !cam) return false
    // Native captured format — set verbatim, no math.
    if (cam.orbit) {
        mv.cameraOrbit = cam.orbit
        if (cam.target) mv.cameraTarget = cam.target
        if (cam.fieldOfView) mv.fieldOfView = cam.fieldOfView
        return true
    }
    // Legacy Blender-exported format.
    const orbit = orbitFromCameraData(cam)
    if (!orbit) return false
    mv.cameraOrbit = orbit
    return true
}

const PlaydynamexNew = ({
    data: dataProp,
    projectId: propProjectId,
    screenId: propScreenId,
    initialStep,
}) => {
    const history = useHistory()

    // --- NEW: Parse URL inside the component so it updates on route change ---
    const urlParams = useMemo(
        () =>
            new URLSearchParams(
                history.location?.search || window.location.search
            ),
        [history.location?.search]
    )
    const isPreviewMode = urlParams.get('preview') === 'true'
    const PROJECT_ID =
        propProjectId || urlParams.get('projectId') || 'talking-dragon'
    const screenId = propScreenId || urlParams.get('screenId') || null

    const makeCtx = useContext(MakeContext)
    const isMake = !!makeCtx
    const { setHeaderProps, setFooterProps, setToolbarProps } = makeCtx || {}
    const isShellMode = isMake && !!setHeaderProps

    const HeaderComponent = MakeHeader
    const audioRef = useRef(null)
    const appContainerRef = useRef(null)
    const modelViewerRef = useRef(null)
    const isModelLoadedRef = useRef(false)
    const cameraImportRef = useRef(null)

    // STATE
    const storageKey = `playdynamex_step_${PROJECT_ID}_${screenId || 'default'}`
    const [transcript, setTranscript] = useState(null)
    const [loading, setLoading] = useState(true)

    const [currentStep, setCurrentStep] = useState(() => {
        if (isPreviewMode) return 1

        // PRIORITY 1: Explicit jump from Timeline
        if (initialStep) {
            sessionStorage.setItem(storageKey, initialStep.toString())
            return initialStep
        }

        // PRIORITY 2: Memory from last visit
        const savedStep = sessionStorage.getItem(storageKey)
        const parsed = parseInt(savedStep, 10)
        return !isNaN(parsed) && parsed > 0 ? parsed : 1
    })
    // const [showSubtitles, setShowSubtitles] = useState(false)
    const [aiVoiceover, setAiVoiceover] = useState(
        () => sessionStorage.getItem('aiVoiceToggle') !== 'false'
    )
    const [showMaterials, setShowMaterials] = useState(
        () => sessionStorage.getItem('materialsToggle') !== 'false'
    )

    // --- Camera-capture (preview only) ---
    // Keyed by stepNumber → { orbit, target, fieldOfView } read straight from
    // the model-viewer, so what the designer sees is exactly what gets saved.

    const [capturedCameras, setCapturedCameras] = useState({})
    // Transient: the step just (re)captured — drives a brief green "✓ Saved!"
    // flash on the button so the admin gets confirmation the click registered.
    const [flashStep, setFlashStep] = useState(null)
    // "Copy camera from step N" input value.
    const [copyFromStep, setCopyFromStep] = useState('')

    // ── Feature state: step popup ──
    const [showStepPopup, setShowStepPopup] = useState(false)

    // ── Feature state: device gate ──
    // true once user connects device on a step that requires it
    const [deviceGateUnlocked, setDeviceGateUnlocked] = useState(false)

    // ── Feature state: byte transfer ──
    const [isSendingBytes, setIsSendingBytes] = useState(false)
    const [bytesSentOk, setBytesSentOk] = useState(false)

    // ── Rdone gate — mirrors PlayScreen's TACK-GATE ──
    // True while we're waiting for the device's "Rdone" reply after a waitForAck send.
    const awaitingRdoneRef = useRef(false)
    const [isAwaitingRdone, setIsAwaitingRdone] = useState(false)
    const rdoneFallbackRef = useRef(null)   // 5s safety-net timer
    const lastBytesSentAtRef = useRef(0)    // timestamp, for busy-after-release check
    const wantsAckRef = useRef(false)       // true only when current send has waitForAck on
    const ackStringRef = useRef('Rdone')    // custom ack string from admin, defaults to 'Rdone'

    // webSerial + worker from Redux — same pattern as Play mode's live controls.
    // PlaydynamexNew never dispatches serial actions; connection is triggered by
    // the USB button already in the header.
    const webSerial = useSelector((state) => state.webSerial)
    const worker = useSelector((state) => state.worker)
    // Stable refs so step-change effects always read the latest values without
    // adding them as deps (avoids re-triggering on every connect/disconnect).
    const webSerialRef = useRef(webSerial)
    const workerRef = useRef(worker)
    useEffect(() => { webSerialRef.current = webSerial }, [webSerial])
    useEffect(() => { workerRef.current = worker }, [worker])

    // Track whether we've already sent the WOOF init for this session
    const woofSentRef = useRef(false)
    // Timestamp when WOOF process started — used to delay step bytes until device is in play mode
    const woofStartedAtRef = useRef(0)

    // Seed the capture state from camera data ALREADY saved on the steps (the
    // JSON that was previously captured → downloaded → uploaded to staging).
    // This makes the capture → download → re-upload loop lossless: an admin can
    // come back later, see the existing coverage (e.g. "50/100 captured"), edit
    // or add only the remaining steps, and Download a COMPLETE json in one go —
    // instead of starting empty and nulling the steps they didn't re-capture.
    useEffect(() => {
        if (!transcript?.steps) return
        const seed = {}
        transcript.steps.forEach((step) => {
            if (step?.cameraData) seed[step.stepNumber] = step.cameraData
        })
        setCapturedCameras(seed)
    }, [transcript])

    const playAudioForStep = useCallback(
        (stepNumber) => {
            if (!transcript?.meta?.audioBaseUrl) return

            const audioUrl = `${transcript.meta.audioBaseUrl}${stepNumber}.mp3?t=${Date.now()}`

            // Make mode: play through the shell's persistent, pre-unlocked
            // audio element so playback survives cross-screen navigation.
            if (makeCtx?.playAudio) {
                makeCtx.playAudio(audioUrl)
                return
            }

            if (!audioRef.current) return
            audioRef.current.src = audioUrl
            audioRef.current
                .play()
                .catch((e) => console.log('Playback interrupted or failed:', e))
        },
        [transcript, makeCtx]
    )

    // 1. FETCH DATA ON MOUNT (skip if data provided via props)
    useEffect(() => {
        if (dataProp) {
            setTranscript(dataProp)
            setLoading(false)
            return
        }
        const fetchTranscript = async () => {
            try {
                let endpoint = isPreviewMode
                    ? `${BACKEND_URL}/api/staging/playdynamex/${PROJECT_ID}`
                    : `${BACKEND_URL}/api/playdynamex/${PROJECT_ID}`
                if (screenId) endpoint += `/${screenId}`
                const res = isPreviewMode
                    ? await fetch(endpoint, { credentials: 'include' })
                    : await fetch(endpoint)
                if (!res.ok) throw new Error('Failed to fetch transcript')
                const data = await res.json()
                setTranscript(data)
                setLoading(false)
            } catch (error) {
                console.error('Error loading project:', error)
                setLoading(false)
            }
        }
        fetchTranscript()
    }, [dataProp])

    // 2. TRIGGER AUDIO ONLY AFTER LOADING IS COMPLETE
    useEffect(() => {
        if (!loading && transcript && aiVoiceover) {
            playAudioForStep(currentStep)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, transcript]) // only on initial load

    // COMPUTED VALUES
    const totalSteps = useMemo(
        () => transcript?.steps?.length || 0,
        [transcript]
    )

    // const modelPath = useMemo(() => '/models/test4.glb', [])
    const modelPath = useMemo(
        () => `${transcript?.meta?.modelPath}`,
        [transcript]
    )

    // 1. FIRST, find the data for the current step
    const currentStepData = useMemo(() => {
        if (!transcript) return null
        return transcript.steps.find((step) => step.stepNumber === currentStep)
    }, [currentStep, transcript])

    // 2. THEN, pull the actual animation name from that step
    const currentAnimationName = useMemo(() => {
        if (!currentStepData) return undefined

        // Look for the specific animation name we saved from the Admin Panel
        if (currentStepData.animationName) {
            return currentStepData.animationName
        }

        // Fallback for older projects that haven't been updated in the admin panel yet
        return currentStep.toString().padStart(2, '0')
    }, [currentStepData, currentStep])

    // Handle model load: set timeScale, mark model as ready, and kick off initial playback.
    // Depends on `loading` so it re-runs once model-viewer is actually in the DOM,
    // and on `modelPath` so it re-registers after a model swap.
    useEffect(() => {
        if (loading) return
        const mv = modelViewerRef.current
        if (!mv) return

        isModelLoadedRef.current = false 

        const handleLoad = () => {
            // FIX: Safely parse as Number, fallback strictly to 1.0 for older projects
            const targetSpeed = currentStepData?.speed !== undefined ? Number(currentStepData.speed) : 1.0;
            mv.timeScale = targetSpeed > 0 ? targetSpeed : 1.0;

            isModelLoadedRef.current = true
            // Frame the current step's viewpoint as soon as the model is ready.
            // The step-change effect below can't do this on first load because
            // it runs before `load` fires (isModelLoadedRef still false), so the
            // first step would otherwise keep the default view until you navigate.
            const cam =
                capturedCameras[currentStep] || currentStepData?.cameraData
            applyCameraToViewer(mv, cam)
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const m = modelViewerRef.current
                    if (!m) return
                    m.currentTime = 0
                    m.play({ repetitions: 1 })
                })
            })
        }

        mv.addEventListener('load', handleLoad)
        return () => mv.removeEventListener('load', handleLoad)
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, modelPath]) // <-- CRITICAL: currentStepData removed from here

    // Play once and freeze on the last frame whenever the step (animation) changes.
    // Skips silently if the model isn't loaded yet — the handleLoad above covers
    // the initial play, so there is no gap.
    // Play once and freeze on the last frame whenever the step (animation) changes.
    useEffect(() => {
        const mv = modelViewerRef.current
        if (!mv || !currentAnimationName || !isModelLoadedRef.current) return

        let raf1, raf2
        let isCancelled = false

        raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                if (isCancelled) return
                const m = modelViewerRef.current
                if (!m) return
                m.currentTime = 0
                
                // Read speed from current step data, default strictly to 1.0 if missing
                const targetSpeed = currentStepData?.speed !== undefined ? Number(currentStepData.speed) : 1.0;
                m.timeScale = targetSpeed > 0 ? targetSpeed : 1.0;
                
                m.play({ repetitions: 1 }) 
            })
        })

        return () => {
            isCancelled = true
            cancelAnimationFrame(raf1)
            cancelAnimationFrame(raf2)
        }
    }, [currentAnimationName, currentStepData]) // <-- Keep currentStepData here

    useEffect(() => {
        const mv = modelViewerRef.current
        if (!mv || !isModelLoadedRef.current) return

        // Prefer the in-session captured/imported camera for this step, so an
        // imported JSON (which updates capturedCameras, not the saved step data)
        // is reflected in the preview immediately. Falls back to the saved step
        // data; no-ops when neither exists.
        const cam = capturedCameras[currentStep] || currentStepData?.cameraData
        if (!cam) return
        // Delay the camera switch so the model animation can begin playing before
        // the viewpoint snaps — avoids jarring immediate jumps on step navigation.
        const timer = setTimeout(() => {
            applyCameraToViewer(modelViewerRef.current, cam)
        }, 400)
        return () => clearTimeout(timer)
    }, [currentStepData, capturedCameras, currentStep])

    // Floating subtitle state
    const [showSubtitleDisplay, setShowSubtitleDisplay] = useState(false)
    const [subtitleVisible, setSubtitleVisible] = useState(false)
    const [typewriterText, setTypewriterText] = useState('')
    const subtitleRef = useRef(null)
    const [aiSpeaking, setAiSpeaking] = useState(true)

    // Refined Typewriter Effect
    useEffect(() => {
        if (aiVoiceover && currentStepData?.instruction) {
            setTypewriterText('')
            setAiSpeaking(true) // AI is now "active"

            let i = 0
            const text = currentStepData.instruction
            const interval = setInterval(() => {
                i++
                setTypewriterText(text.slice(0, i))

                if (i >= text.length) {
                    clearInterval(interval)
                    // Keep aiSpeaking true if the audio is still playing,
                    // or set to false if you just want it to represent the text animation
                    setAiSpeaking(true)
                }
            }, 40) // 40ms is usually the sweet spot for readability
            return () => clearInterval(interval)
        } else {
            setTypewriterText(currentStepData?.instruction || '')
            setAiSpeaking(false)
        }
    }, [aiVoiceover, currentStepData])

    // Hide subtitle display on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (
                subtitleRef.current &&
                !subtitleRef.current.contains(e.target)
            ) {
                setShowSubtitleDisplay(false)
                setAiSpeaking(true)
            }
        }
        if (showSubtitleDisplay) {
            document.addEventListener('mousedown', handleClick)
        }
        return () => {
            document.removeEventListener('mousedown', handleClick)
        }
    }, [showSubtitleDisplay])

    // Slide in subtitle on mount
    useEffect(() => {
        const t = setTimeout(() => {
            setSubtitleVisible(true)
            setShowSubtitleDisplay(true)
        }, 50)
        return () => clearTimeout(t)
    }, [])

    useEffect(() => {
        sessionStorage.setItem(storageKey, currentStep.toString())
    }, [currentStep, storageKey])

    // ── Device init: send LS + WOOF exactly like PlayScreen does ──
    // PlayScreen sends two packets before any W-commands work:
    //   1. settingbytes / Live Start [76, 83, 79, ...] — initialises live mode
    //   2. WOOF [87, 79, 79, 70] after 3s — puts device into app/play mode
    // Without these, the device ignores custom W-byte commands entirely.
    useEffect(() => {
        const w = workerRef.current
        if (!w || !webSerial?.isConnected) {
            woofSentRef.current = false  // reset on disconnect so it re-inits on next connect
            return
        }
        if (woofSentRef.current) return
        woofSentRef.current = true
        woofStartedAtRef.current = Date.now()

        // Build Live Start (LS) packet for the connected board.
        // Port count determines array length (mirrors buildLiveStartBytes in ByteRecorderPopup).
        const connectedDevice = sessionStorage.getItem('connectedDevice') || 'Toki'
        const portCounts = { Poki: 10, Moki: 20, Toki: 18 }
        const len = (portCounts[connectedDevice] ?? 18) + 2
        const lsBytes = Array(len).fill(79)  // fill with 'O' (79)
        lsBytes[0] = 76  // 'L'
        lsBytes[1] = 83  // 'S'

        const woofBytes = [87, 79, 79, 70]  // 'W','O','O','F'

        // Send LS immediately, WOOF after 3s (same timing as PlayScreen)
        w.postMessage({ type: 'writeArray', value: lsBytes })
        console.log('[PDX] LS sent:', lsBytes)

        const timer = setTimeout(() => {
            workerRef.current?.postMessage({ type: 'writeArray', value: woofBytes })
            console.log('[PDX] WOOF sent — device now in play mode')
        }, 3000)

        return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webSerial?.isConnected])

    // ── Rdone gate helpers (same logic as PlayScreen TACK-GATE) ──
    // Must be defined BEFORE handleSendBytes which depends on holdRdoneGate.
    const releaseRdoneGate = useCallback((reason) => {
        console.log(`[PDX Rdone] RELEASED (${reason})`)
        if (rdoneFallbackRef.current) {
            clearTimeout(rdoneFallbackRef.current)
            rdoneFallbackRef.current = null
        }
        awaitingRdoneRef.current = false
        setIsAwaitingRdone(false)
    }, [])

    const holdRdoneGate = useCallback(() => {
        awaitingRdoneRef.current = true
        setIsAwaitingRdone(true)
        if (rdoneFallbackRef.current) clearTimeout(rdoneFallbackRef.current)
        rdoneFallbackRef.current = setTimeout(() => releaseRdoneGate('fallback'), 5000)
    }, [releaseRdoneGate])

    // Listen on the serial worker for Rdone / busy — same event pattern as PlayScreen
    useEffect(() => {
        const w = workerRef.current
        if (!w) return
        const handleWorkerMessage = (e) => {
            const msg = e.data || {}
            let str = ''
            if (msg.type === 'read') str = msg.value
            else if (msg.type === 'actionRead') str = msg.value?.stringData
            else return
            if (typeof str !== 'string') return
            const ackStr = ackStringRef.current || 'Rdone'
            const hasAck = str.includes(ackStr)
            const hasBusy = str.includes('busy')
            if (!hasAck && !hasBusy) return
            console.log(`[PDX Rdone] serial=${JSON.stringify(str)} ack(${ackStr})=${hasAck} busy=${hasBusy} waiting=${awaitingRdoneRef.current}`)
            if (awaitingRdoneRef.current) {
                hasBusy ? holdRdoneGate() : releaseRdoneGate(ackStr)
            } else if (hasBusy && wantsAckRef.current && Date.now() - lastBytesSentAtRef.current < 6000) {
                holdRdoneGate()
            }
        }
        w.addEventListener('message', handleWorkerMessage)
        return () => w.removeEventListener('message', handleWorkerMessage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [worker])

    // ── Byte transfer helper ──
    // Parses cfg.liveBytes string (same format as Play mode: "W1" → [87, 1])
    // then sends via the serial worker — identical path to LiveTackButtonControl:
    //   parseLiveBytes(str) → worker.postMessage({ type: 'writeArray', value: array })
    const handleSendBytes = useCallback(async (cfg) => {
        const w = workerRef.current
        const ws = webSerialRef.current
        if (!w || !ws?.isConnected || !cfg?.liveBytes) {
            console.log('[PDX Bytes] skipped — worker:', !!w, 'connected:', ws?.isConnected, 'liveBytes:', cfg?.liveBytes)
            return
        }
        const byteArray = parseLiveBytes(cfg.liveBytes)
        if (!byteArray.length) return
        console.log('[PDX Bytes] sending', cfg.liveBytes, '→', byteArray)
        setIsSendingBytes(true)
        setBytesSentOk(false)
        try {
            w.postMessage({ type: 'writeArray', value: byteArray })
            lastBytesSentAtRef.current = Date.now()
            wantsAckRef.current = !!cfg.waitForAck
            // Build the ack pattern: string mode → substring; bytes mode → char string
            // from comma-separated decimal bytes (e.g. "0,82,100,111,110,101,0" → "\0Rdone\0").
            if (cfg.ackMode === 'bytes') {
                const nums = (cfg.ackBytes || '')
                    .split(',')
                    .map((s) => parseInt(s.trim(), 10))
                    .filter((n) => !isNaN(n) && n >= 0 && n <= 255)
                ackStringRef.current = nums.length > 0 ? String.fromCharCode(...nums) : 'Rdone'
            } else {
                ackStringRef.current = cfg.ackString?.trim() || 'Rdone'
            }
            // Arm Rdone gate before showing success — gate will hold Next locked
            // until device replies with the configured ack string.
            if (cfg.waitForAck) {
                holdRdoneGate()
            }
            setBytesSentOk(true)
            setTimeout(() => setBytesSentOk(false), 2000)
        } catch (e) {
            console.error('[PDX Bytes] failed:', e)
        } finally {
            setIsSendingBytes(false)
        }
    }, [holdRdoneGate])

    // ── Effect: step entry — show popup + send onEnter bytes ──
    // Deps include currentStepData so the effect re-fires when transcript first
    // loads on step 1 (at mount currentStepData is null; without this dep the
    // effect would silently early-return and never show step-1's popup).
    useEffect(() => {
        // Reset per-step transient state

        setShowStepPopup(false)
        setBytesSentOk(false)
        wantsAckRef.current = false
        releaseRdoneGate('step change')

        if (!currentStepData) return

        // If device is already connected when entering this step, unlock gate immediately.
        // The reconnect effect only fires on isConnected *change*, so it won't help here.
        if (webSerialRef.current?.isConnected) {
            setDeviceGateUnlocked(true)
        } else {
            setDeviceGateUnlocked(false)
        }

        // Show instruction popup if configured for auto entry
        const popup = currentStepData.stepPopup
        if (popup?.enabled && (popup?.showOn || 'onEnter') === 'onEnter') {
            setShowStepPopup(true)
        }

        // Send bytes on entry — delay if WOOF hasn't had time to reach the device.
        // WOOF fires 3s after connect; we wait 3.5s total. If the step-entry effect
        // fires while we're still inside that window (e.g. step 1 + transcript just
        // loaded), wait for the remaining time before sending so the device is
        // already in play mode when the bytes arrive.
        const cfg = currentStepData.byteConfig
        if (cfg?.enabled && (cfg?.trigger || 'onEnter') === 'onEnter' && cfg?.liveBytes) {
            if (webSerialRef.current?.isConnected && woofSentRef.current) {
                const elapsed = Date.now() - woofStartedAtRef.current
                const delay = Math.max(0, 3500 - elapsed)
                if (delay > 0) {
                    const t = setTimeout(() => handleSendBytes(cfg), delay)
                    return () => clearTimeout(t)
                } else {
                    handleSendBytes(cfg)
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep, currentStepData])

    // ── Effect: device connect / disconnect mid-step ──
    useEffect(() => {
        if (!currentStepData) return

        if (!webSerial?.isConnected) {
            // Device disconnected — re-lock gate so Next is blocked again
            const gate = currentStepData.deviceGate
            if (gate?.enabled) {
                setDeviceGateUnlocked(false)
        
            }
            return
        }

        // Device connected — unlock gate + send onEnter bytes after WOOF
        const cfg = currentStepData.byteConfig
        const gate = currentStepData.deviceGate

        if (gate?.enabled && !deviceGateUnlocked) {
            setDeviceGateUnlocked(true)
            if (gate.ackMessage) {
            }
        }

        const timer = setTimeout(() => {
            if (cfg?.enabled && (cfg?.trigger || 'onEnter') === 'onEnter' && cfg?.liveBytes) {
                handleSendBytes(cfg)
            }
        }, 3500)

        return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [webSerial?.isConnected])

    const resetTutorial = useCallback(() => {
        setCurrentStep(1)
        setShowSubtitleDisplay(true)
        setAiSpeaking(true)
        if (aiVoiceover) {
            playAudioForStep(1)
        }
    }, [aiVoiceover, playAudioForStep])

    // --- Camera capture: record the CURRENT viewer camera for this step ---
    // Reads model-viewer's live spherical camera (radians+meters) and stores it
    // as a ready-to-set camera-orbit string plus target/FOV — no Blender→web
    // conversion, so it can't drift out of alignment.
    const captureCurrentPosition = useCallback(() => {
        const mv = modelViewerRef.current
        if (!mv || typeof mv.getCameraOrbit !== 'function') {
            alert('Viewer not ready yet — give the model a moment to load.')
            return
        }
        const o = mv.getCameraOrbit() // { theta, phi (rad), radius (m) }
        const t = mv.getCameraTarget() // { x, y, z (m) }
        const fov = mv.getFieldOfView() // degrees
        const deg = (rad) => ((rad * 180) / Math.PI).toFixed(2)
        const entry = {
            orbit: `${deg(o.theta)}deg ${deg(o.phi)}deg ${o.radius.toFixed(3)}m`,
            target: `${t.x.toFixed(3)}m ${t.y.toFixed(3)}m ${t.z.toFixed(3)}m`,
            fieldOfView: `${Number(fov).toFixed(2)}deg`,
        }
        setCapturedCameras((prev) => ({ ...prev, [currentStep]: entry }))
        // Brief visual confirmation the capture registered.
        setFlashStep(currentStep)
        setTimeout(() => setFlashStep(null), 900)
    }, [currentStep])

    // --- Camera: copy another step's camera onto the current step ---
    // Lets the admin reuse a framed viewpoint (e.g. "same as step 5") instead of
    // re-framing manually. Applies it to the viewer immediately for confirmation.
    const copyCameraFromStep = useCallback(() => {
        const src = Number(copyFromStep)
        if (!src || src < 1 || src > totalSteps || src === currentStep) {
            alert(`Enter a valid step number (1–${totalSteps}, not this step).`)
            return
        }
        const srcCam = capturedCameras[src]
        if (!srcCam) {
            alert(`Step ${src} has no camera data to copy yet.`)
            return
        }
        setCapturedCameras((prev) => ({ ...prev, [currentStep]: srcCam }))
        applyCameraToViewer(modelViewerRef.current, srcCam)
        setFlashStep(currentStep)
        setTimeout(() => setFlashStep(null), 900)
    }, [copyFromStep, capturedCameras, currentStep, totalSteps])

    // --- Camera capture: download all captured steps as an uploadable JSON ---
    // Emits a positional camera_data array (index 0 = step 1) matching the
    // admin's camera-JSON uploader, so it re-imports with no extra plumbing.
    // Uncaptured steps become null (viewer just leaves those views untouched).
    const downloadCameraJson = useCallback(() => {
        const camera_data = []
        for (let s = 1; s <= totalSteps; s++) {
            camera_data.push(capturedCameras[s] || null)
        }
        const blob = new Blob([JSON.stringify({ camera_data }, null, 2)], {
            type: 'application/json',
        })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `camera_data_${PROJECT_ID}.json`
        a.click()
        URL.revokeObjectURL(url)
    }, [capturedCameras, totalSteps, PROJECT_ID])

    // --- Camera capture: import an existing camera JSON into the capture state ---
    // MERGES positionally (index 0 = step 1) into whatever's already captured, so
    // the admin can load a prior file, then edit/add only the steps they need and
    // Download the complete set. null entries are skipped (never overwrite).
    const importCameraJson = useCallback((e) => {
        const file = e.target.files[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = (ev) => {
            try {
                const parsed = JSON.parse(ev.target.result)
                const arr = parsed.camera_data
                if (!Array.isArray(arr)) {
                    throw new Error('No camera_data array found in the file.')
                }
                setCapturedCameras((prev) => {
                    const next = { ...prev }
                    arr.forEach((entry, idx) => {
                        if (entry) next[idx + 1] = entry // idx 0 → step 1
                    })
                    return next
                })
                alert(
                    `✅ Imported camera data for ${arr.filter(Boolean).length} step(s). Edit/add as needed, then Download.`
                )
            } catch (err) {
                alert(`❌ Could not import camera JSON: ${err.message}`)
            }
        }
        reader.readAsText(file)
        e.target.value = ''
    }, [])

    // NAVIGATION HANDLERS
    const handleNext = useCallback(async () => {
        // Device gate: block navigation if admin required connection and it's not done
        const gate = currentStepData?.deviceGate
        if (gate?.enabled && !deviceGateUnlocked) {
            return
        }

        // Byte transfer: send bytes on "Next" click if trigger is onComplete.
        // Block if already awaiting Rdone from a previous send.
        const cfg = currentStepData?.byteConfig
        if (cfg?.enabled && cfg?.trigger === 'onComplete' && cfg?.liveBytes) {
            if (webSerialRef.current?.isConnected && woofSentRef.current && !awaitingRdoneRef.current) {
                handleSendBytes(cfg)
                // If waitForAck, gate is now armed — stay on this step until Rdone
                if (cfg.waitForAck) return
            }
        }

        if (currentStep < totalSteps) {
            const globalStep = ContentEngine.getCurrentScreenOffset() + currentStep
            ContentEngine.tryEarnStep(globalStep)
            setCurrentStep(currentStep + 1)

            // Trigger Audio if Voiceover is ON
            if (aiVoiceover) {
                playAudioForStep(currentStep + 1)
            }
        } else {
            // Last step — earn it before navigating away
            const globalStep = ContentEngine.getCurrentScreenOffset() + currentStep
            ContentEngine.tryEarnStep(globalStep)

            if (isPreviewMode) return

            sessionStorage.removeItem(storageKey)

            if (dataProp) {
                ContentEngine.next()
            } else {
                history.push('/AssemblyTwo')
                sessionStorage.setItem('connectedDevice', 'Toki')
                sessionStorage.setItem('deviceVersion', '1')
            }
        }

        setAiSpeaking(false)
        setShowSubtitleDisplay(true)
    }, [currentStep, totalSteps, aiVoiceover, playAudioForStep, history, isPreviewMode, storageKey, dataProp])

    const handlePrev = useCallback(() => {
        if (currentStep > 1) {
            const prevStep = currentStep - 1
            setCurrentStep(prevStep)

            if (aiVoiceover) {
                playAudioForStep(prevStep)
            }
        } else if (dataProp) {
            // At step 1 in Make mode — go to the last step of the previous screen
            ContentEngine.prev()
        }

        setAiSpeaking(false)
        setShowSubtitleDisplay(true)
    }, [currentStep, aiVoiceover, playAudioForStep, dataProp])

    const goBack = useCallback(() => {
        if (dataProp) {
            ContentEngine.prevToStart()
        } else {
            history.goBack()
        }
    }, [history, dataProp])

    const handleFullscreen = useCallback(() => {
        // In Make mode, fullscreen the parent wrapper so it persists across screen transitions
        const el = isMake
            ? document.querySelector('.make-page')
            : appContainerRef.current

        const isFullscreen =
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.msFullscreenElement

        if (!isFullscreen) {
            if (el?.requestFullscreen) el.requestFullscreen()
            else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen()
            else if (el?.msRequestFullscreen) el.msRequestFullscreen()
        } else {
            if (document.exitFullscreen) document.exitFullscreen()
            else if (document.webkitExitFullscreen)
                document.webkitExitFullscreen()
            else if (document.msExitFullscreen) document.msExitFullscreen()
        }
    }, [isMake])

    const handleHeaderTabClick = useCallback(
        (index) => {
            if (index > 3) {
                return
            }
            if (index === 0) {
                history.push('/playdynamexNew')
            }
            if (index === 1) {
                sessionStorage.setItem('connectedDevice', 'Toki')
                sessionStorage.setItem('deviceVersion', '1')
                history.push('/assemblyTwo')
            }
            if (index === 2) {
                history.push('/logicTwo')
            }
            if (index === 3) {
                history.push('/SimulateNew')
            }
        },
        [history]
    )

    // TOGGLE VOICEOVER
    const handleAiVoiceoverToggle = useCallback(() => {
        const nextState = !aiVoiceover
        setAiVoiceover(nextState)
        sessionStorage.setItem('aiVoiceToggle', String(nextState))

        if (nextState) {
            playAudioForStep(currentStep)
        } else {
            if (makeCtx?.stopAudio) {
                makeCtx.stopAudio()
            } else if (audioRef.current) {
                audioRef.current.pause()
                audioRef.current.currentTime = 0
            }
        }
    }, [aiVoiceover, currentStep, playAudioForStep, makeCtx])

    // TOGGLE MATERIALS PANEL
    const handleMaterialsToggle = useCallback(() => {
        setShowMaterials((prev) => {
            const nextState = !prev
            sessionStorage.setItem('materialsToggle', String(nextState))
            return nextState
        })
    }, [])

    // Stable onStepClick for timeline nav (needed as shell header prop dep)
    const onStepClick = useCallback(
        (stepNum) => {
            ContentEngine.breakSequentialChain()
            setCurrentStep(stepNum)
            if (aiVoiceover) playAudioForStep(stepNum)
            setAiSpeaking(false)
            setShowSubtitleDisplay(true)
        },
        [aiVoiceover, playAudioForStep]
    )

    // --- Shell: register header props whenever relevant state changes ---
    useEffect(() => {
        if (!setHeaderProps) return
        setHeaderProps({
            title: transcript?.title || '',
            showJumpTo: false,
            titlePosition: 'left',
            help_btnPosition: 'right',
            isGoBack: true,
            goBack,
            showHelp: true,
            showBluetoothBtn: true,
            showUsbBtn: true,
            currentStep,
            totalSteps,
            stepTitles: transcript?.steps?.map((s) => s?.title || '') || [],
            onStepClick,
        })
    }, [
        setHeaderProps,
        transcript,
        currentStep,
        totalSteps,
        goBack,
        onStepClick,
    ])

    // --- Shell: register footer props whenever relevant state changes ---
    useEffect(() => {
        if (!setFooterProps) return
        const gateBlocking =
            currentStepData?.deviceGate?.enabled &&
            !deviceGateUnlocked
        setFooterProps({
            stepCounter: `Step ${currentStep} of ${totalSteps}`,
            stepTitle: currentStepData?.title || '',
            prevDisabled: dataProp ? false : currentStep === 1,
            nextLabel: currentStep === totalSteps ? 'Continue' : 'Next',
            nextDisabled: gateBlocking || isSendingBytes || isAwaitingRdone,
            onPrev: handlePrev,
            onNext: handleNext,
        })
    }, [
        setFooterProps,
        currentStep,
        totalSteps,
        currentStepData,
        deviceGateUnlocked,
        isSendingBytes,
        handlePrev,
        handleNext,
        dataProp,
    ])

    // --- Shell: register toolbar props ---
    useEffect(() => {
        if (!setToolbarProps) return
        setToolbarProps({
            aiVoiceover,
            onAiToggle: () => {
                handleAiVoiceoverToggle()
                // Keep the instruction text displayed regardless of voiceover —
                // the AI toggle controls only the voice, not the text.
                setSubtitleVisible(true)
                setShowSubtitleDisplay(true)
            },
            showMaterials,
            onMaterialsToggle: handleMaterialsToggle,
            onFullscreen: handleFullscreen,
            onReset: resetTutorial,
            // ℹ manual popup button support in shell mode
            stepPopupEnabled: !!(currentStepData?.stepPopup?.enabled && currentStepData?.stepPopup?.showOn === 'manual'),
            onShowStepPopup: () => setShowStepPopup(true),
        })
    }, [
        setToolbarProps,
        aiVoiceover,
        handleAiVoiceoverToggle,
        showMaterials,
        handleMaterialsToggle,
        handleFullscreen,
        resetTutorial,
        currentStepData?.stepPopup?.enabled,
        currentStepData?.stepPopup?.showOn,
    ])

    // RENDER LOADING STATE
    if (loading) {
        return (
            <div
                className="pdx-app-container"
                style={{
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                Loading Project...
            </div>
        )
    }

    if (!transcript) {
        return (
            <div className="pdx-app-container">Error loading project data.</div>
        )
    }

    // Derived: is the Next button locked by the device gate?
    const nextGateLocked =
        currentStepData?.deviceGate?.enabled &&
        !deviceGateUnlocked

    return (
        <div className="pdx-app-container" ref={appContainerRef}>

            {/* ── STEP POPUP MODAL ── */}
            {showStepPopup && currentStepData?.stepPopup?.enabled && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'hsla(0,0%,100%,.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10000,
                        fontFamily: 'Halcyon_SemiBold',
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            width: '90%',
                            maxWidth: '400px',
                            minHeight: '150px',
                            padding: '1.5rem 2rem',
                            background: 'linear-gradient(to right, #f0eded, white)',
                            borderRadius: '3rem',
                            border: '1px solid white',
                            boxShadow: '0.5rem 0.5rem 2rem #d9d5d4',
                            animation: 'pdx-popup-in 0.22s ease',
                        }}
                    >
                        {/* Close button — top-right diagonal corner */}
                        <button
                            onClick={() => setShowStepPopup(false)}
                            style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                width: '2.2rem',
                                height: '2.2rem',
                                background: '#e57373',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 12px rgba(229,115,115,0.4)',
                                padding: '0',
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L11 11M11 1L1 11" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
                            </svg>
                        </button>

                        {currentStepData.stepPopup.imageUrl && (
                            <img
                                src={currentStepData.stepPopup.imageUrl}
                                alt="Step Instruction"
                                style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', marginBottom: '16px', borderRadius: '12px' }}
                            />
                        )}

                        <h2
                            style={{
                                margin: '0 0 10px',
                                color: '#1f1f4e',
                                fontSize: '1.3rem',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                                textAlign: 'center',
                            }}
                        >
                            {currentStepData.stepPopup.title || `Step ${currentStep} — Instructions`}
                        </h2>
                        <p
                            style={{
                                color: '#444',
                                lineHeight: 1.65,
                                margin: '0',
                                fontSize: '1rem',
                                whiteSpace: 'pre-wrap',
                                textAlign: 'center',
                            }}
                        >
                            {currentStepData.stepPopup.message}
                        </p>
                    </div>
                </div>
            )}

            {/* --- NEW: Floating Exit Preview Button --- */}
            {isPreviewMode && (
                <button
                    onClick={() => history.goBack()}
                    style={{
                        position: 'fixed',
                        top: '12vh', // Just below your 10vh header
                        right: '30px',
                        zIndex: 9999,
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '50px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 4px 15px rgba(239, 68, 68, 0.4)',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <MdOutlineArrowBack size={18} /> Exit Preview
                </button>
            )}
            {/* --- Camera-capture panel (preview only) --- */}
            {isPreviewMode && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '24px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        background: 'rgba(23, 23, 43, 0.92)',
                        color: 'white',
                        padding: '10px 16px',
                        borderRadius: '50px',
                        boxShadow: '0 6px 24px rgba(0, 0, 0, 0.35)',
                        backdropFilter: 'blur(8px)',
                        fontSize: '14px',
                    }}
                >
                    <button
                        onClick={captureCurrentPosition}
                        style={{
                            // Three states, each a distinct colour:
                            //   just clicked → bright green "✓ Saved!" (feedback)
                            //   already captured → amber "Recapture"
                            //   not captured yet → indigo "Capture"
                            background:
                                flashStep === currentStep
                                    ? '#16a34a'
                                    : capturedCameras[currentStep]
                                      ? '#f59e0b'
                                      : '#6366f1',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '50px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'background 0.15s ease',
                        }}
                    >
                        <MdOutlineViewInAr size={18} />
                        {flashStep === currentStep
                            ? '✓ Saved!'
                            : capturedCameras[currentStep]
                              ? `Recapture Step ${currentStep}`
                              : `Capture Step ${currentStep}`}
                    </button>
                    <span style={{ opacity: 0.85, whiteSpace: 'nowrap' }}>
                        {Object.keys(capturedCameras).length}/{totalSteps}{' '}
                        captured
                    </span>
                    {/* Copy another step's framed camera onto this step */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <span style={{ opacity: 0.85 }}>Same as step</span>
                        <input
                            type="number"
                            min="1"
                            max={totalSteps}
                            value={copyFromStep}
                            onChange={(e) => setCopyFromStep(e.target.value)}
                            placeholder="#"
                            style={{
                                width: '52px',
                                padding: '8px',
                                borderRadius: '8px',
                                border: 'none',
                                textAlign: 'center',
                                color: '#000',
                            }}
                        />
                        <button
                            onClick={copyCameraFromStep}
                            style={{
                                background: '#334155',
                                color: 'white',
                                border: 'none',
                                padding: '10px 14px',
                                borderRadius: '50px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                            }}
                        >
                            Copy
                        </button>
                    </div>
                    <button
                        onClick={() => cameraImportRef.current?.click()}
                        style={{
                            background: '#7c3aed',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '50px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                        }}
                    >
                        Import JSON
                    </button>
                    <input
                        type="file"
                        accept=".json"
                        ref={cameraImportRef}
                        style={{ display: 'none' }}
                        onChange={importCameraJson}
                    />
                    <button
                        onClick={downloadCameraJson}
                        disabled={Object.keys(capturedCameras).length === 0}
                        style={{
                            background:
                                Object.keys(capturedCameras).length === 0
                                    ? '#3f3f56'
                                    : '#0ea5e9',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '50px',
                            fontWeight: 'bold',
                            cursor:
                                Object.keys(capturedCameras).length === 0
                                    ? 'not-allowed'
                                    : 'pointer',
                        }}
                    >
                        Download JSON
                    </button>
                </div>
            )}
            {/* HEADER — hidden in shell mode (SubprogramRunner renders it persistently) */}
            {!isShellMode && (
                <div className="pdx-header">
                    <HeaderComponent
                        showTimeline={isMake || isPreviewMode}
                        // title={isMake ? 'Beginners' : transcript.title}
                        title={transcript.title}
                        showJumpTo={!isMake && !isPreviewMode}
                        data={{
                            options: [
                                'Mechanical',
                                'Electronic',
                                'Coding',
                                'Simulate',
                            ],
                            selected: 0,
                            isCustomHeader: true,
                        }}
                        onJumpClick={handleHeaderTabClick}
                        titlePosition="left"
                        help_btnPosition="right"
                        isGoBack={true}
                        goBack={goBack}
                        showHelp={true}
                        showBluetoothBtn={true}
                        showUsbBtn={true}
                        currentStep={currentStep}
                        totalSteps={totalSteps}
                        stepTitles={
                            transcript?.steps?.map(
                                (step) => step?.title || ''
                            ) || []
                        }
                        onStepClick={(stepNum) => {
                            // stepNum is 1-based from ProgramTimelineNav
                            ContentEngine.breakSequentialChain()
            setCurrentStep(stepNum)
                            if (aiVoiceover) playAudioForStep(stepNum)
                            setAiSpeaking(false)
                            setShowSubtitleDisplay(true)
                        }}
                    />
                </div>
            )}

            {/* VIEWER */}
            <main className="pdx-viewer-container">
                {/* AI Speaking Pill / Subtitle Box — morphs between shapes */}
                {currentStepData?.instruction && (
                    <div
                        ref={subtitleRef}
                        style={{
                            position: 'absolute',
                            top: '10px',
                            left: '50%',
                            transform: !subtitleVisible
                                ? 'translateX(-50%) translateY(-120vh)'
                                : 'translateX(-50%)',
                            maxWidth: !showSubtitleDisplay ? '280px' : '600px',
                            maxHeight: !showSubtitleDisplay ? '44px' : '150px',
                            borderRadius: !showSubtitleDisplay
                                ? '20px'
                                : '20px',
                            padding: !showSubtitleDisplay
                                ? '0 18px'
                                : '12px 24px',
                            background: !showSubtitleDisplay
                                ? 'rgba(60, 60, 120, 0.13)'
                                : 'rgba(255, 255, 255, 0.3)',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                            display: 'grid',
                            gridTemplateRows: !showSubtitleDisplay
                                ? '44px'
                                : 'auto',
                            alignItems: 'center',
                            justifyItems: 'center',
                            overflow: 'hidden',
                            zIndex: 101,
                            cursor: !showSubtitleDisplay
                                ? 'pointer'
                                : 'default',
                            opacity: !subtitleVisible ? 0 : 1,
                            pointerEvents: !subtitleVisible ? 'none' : 'auto',
                            transition: [
                                'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                                'border-radius 0.45s ease',
                                'padding 0.4s ease',
                                'background 0.45s ease',
                                'opacity 0.6s ease',
                            ].join(', '),
                        }}
                        onClick={
                            !showSubtitleDisplay
                                ? () => setShowSubtitleDisplay(true)
                                : undefined
                        }
                    >
                        {/* Pill content — visible when collapsed */}
                        <div
                            style={{
                                gridArea: '1/1',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                fontWeight: 600,
                                fontSize: '1rem',
                                color: '#2f2e65',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                                whiteSpace: 'nowrap',
                                opacity: !showSubtitleDisplay ? 1 : 0,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0.25s'
                                    : '0s',
                                pointerEvents: 'none',
                            }}
                        >
                            <BsStars size={20} style={{ color: '#2f2e65' }} />
                            {aiVoiceover
                                ? 'AI is speaking...'
                                : 'Click to expand'}
                        </div>
                        {/* Subtitle text — visible when expanded */}
                        <div
                            style={{
                                gridArea: '1/1',
                                fontSize: '1.1rem',
                                color: '#333',
                                wordBreak: 'break-word',
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'Halcyon Regular, sans-serif',
                                lineHeight: '1.5',
                                textAlign: 'center',
                                opacity: !showSubtitleDisplay ? 0 : 1,
                                transition: 'opacity 0.2s ease',
                                transitionDelay: !showSubtitleDisplay
                                    ? '0s'
                                    : '0.25s',
                                pointerEvents: 'none',
                                width: '100%',
                            }}
                        >
                            {aiVoiceover
                                ? typewriterText
                                : currentStepData.instruction}
                        </div>
                    </div>
                )}

                <model-viewer
                    key={modelPath} // Force reload when model changes
                    ref={modelViewerRef}
                    src={modelPath}
                    animation-name={currentAnimationName}
                    animation-crossfade-duration="0"
                    camera-controls
                    shadow-intensity="1"
                />

                {/* ── DEVICE GATE BANNER ── */}
                {currentStepData?.deviceGate?.enabled && !deviceGateUnlocked && ReactDOM.createPortal(
                    <div
                        style={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: '10vh',
                            margin: '0 auto',
                            zIndex: 100001,
                            minWidth: '150px',
                            maxWidth: '60vw',
                            width: 'max-content',
                            padding: '14px 24px',
                            backgroundColor: '#f2f4f7',
                            color: '#333333',
                            textAlign: 'center',
                            fontSize: '16px',
                            fontFamily: 'Halcyon_Regular',
                            border: '1px solid #e2e6ea',
                            borderRadius: '12px',
                            boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
                            animation: 'pdx-popup-in 0.35s ease forwards',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                            <MdUsb size={22} color="#555" />
                            <span>
                                {currentStepData.deviceGate.connectInstruction
                                    ? currentStepData.deviceGate.connectInstruction
                                    : <>Connect your <strong>{currentStepData.deviceGate.boardType || 'device'}</strong> to continue</>
                                }
                            </span>
                        </div>
                    </div>,
                    document.body
                )}

                {/* ── BYTE TRANSFER STATUS PILL ── */}
                {(isSendingBytes || bytesSentOk) && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '60px',
                            right: '16px',
                            zIndex: 200,
                            background: bytesSentOk
                                ? 'rgba(27, 94, 32, 0.9)'
                                : 'rgba(23, 23, 43, 0.85)',
                            color: '#fff',
                            padding: '6px 14px',
                            borderRadius: '50px',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            backdropFilter: 'blur(6px)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                    >
                        {bytesSentOk ? (
                            <><MdCheckCircle size={14} /> Bytes sent</>
                        ) : (
                            <>⏳ Sending bytes…</>
                        )}
                    </div>
                )}

                {/* Materials panel for the current step */}
                {currentStepData?.materials && (
                    <div
                        className={`materials-panel ${showMaterials ? 'visible' : ''}`}
                    >
                        {Object.entries(currentStepData.materials).map(
                            ([materialName, quantity]) => (
                                <div
                                    key={materialName}
                                    className="material-item"
                                >
                                    <div className="material-image-wrapper">
                                        <img
                                            src={getBackendImage(`${materialName}.png`)}
                                            alt={materialName}
                                            className="material-image"
                                            onError={(e) => {
                                                e.target.style.display = 'none'
                                            }}
                                        />
                                    </div>
                                    <div className="material-label">
                                        {materialName.replace(/_/g, ' ')} x{' '}
                                        {quantity}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                )}

                {!isShellMode && (
                    <div className="toolbar-sidebar">
                        <button
                            className={`toolbar-button ${showMaterials ? 'active' : ''}`}
                            title="Materials"
                            onClick={handleMaterialsToggle}
                        >
                            <FaShapes />
                        </button>
                        <button
                            className={`toolbar-button ${aiVoiceover ? 'active' : ''}`}
                            title="AI Voiceover"
                            onClick={() => {
                                handleAiVoiceoverToggle()
                                setSubtitleVisible(true)
                                setShowSubtitleDisplay(true)
                            }}
                        >
                            <BsStars />
                        </button>

                        {/* Manual popup info button — only visible when step has a manual popup */}
                        {currentStepData?.stepPopup?.enabled &&
                            currentStepData?.stepPopup?.showOn === 'manual' && (
                                <button
                                    className="toolbar-button"
                                    title="Step Instructions"
                                    onClick={() => setShowStepPopup(true)}
                                    style={{ position: 'relative' }}
                                >
                                    <MdInfoOutline />
                                    <span
                                        style={{
                                            position: 'absolute',
                                            top: '4px',
                                            right: '4px',
                                            width: '7px',
                                            height: '7px',
                                            background: '#f59e0b',
                                            borderRadius: '50%',
                                        }}
                                    />
                                </button>
                            )}

                        <button
                            className="toolbar-button"
                            title="Fullscreen"
                            onClick={handleFullscreen}
                        >
                            <MdFullscreen />
                        </button>

                        <button
                            className="toolbar-button"
                            title="Reset"
                            onClick={resetTutorial}
                        >
                            <RxReset />
                        </button>
                    </div>
                )}

                <audio ref={audioRef} />
            </main>

            {/* FOOTER — hidden in shell mode (SubprogramRunner renders it persistently) */}
            {!isShellMode && (
                <footer className="pdx-footer">
                    <div className="step-counter">
                        Step {currentStep} of {totalSteps}
                    </div>

                    <div className="step-title">{currentStepData?.title}</div>

                    <div className="nav-buttons">
                        <button
                            className="nav-btn"
                            onClick={handlePrev}
                            disabled={dataProp ? false : currentStep === 1}
                        >
                            <GrPrevious />
                            <span className="footer-btn-text">Previous</span>
                        </button>

                        <div
                            style={{ position: 'relative', display: 'inline-flex' }}
                            title={
                                nextGateLocked
                                    ? `Connect your ${currentStepData?.deviceGate?.boardType || 'device'} to proceed`
                                    : undefined
                            }
                        >
                            <button
                                className="nav-btn"
                                onClick={handleNext}
                                disabled={nextGateLocked || isSendingBytes || isAwaitingRdone}
                                style={
                                    nextGateLocked || isSendingBytes || isAwaitingRdone
                                        ? { opacity: 0.55, cursor: 'not-allowed' }
                                        : {}
                                }
                            >
                                <span className="footer-btn-text">
                                    {isAwaitingRdone
                                        ? 'Waiting…'
                                        : isSendingBytes
                                          ? 'Sending…'
                                          : currentStep === totalSteps
                                            ? 'Continue'
                                            : 'Next'}
                                </span>
                                {nextGateLocked
                                    ? <FaLock size={13} />
                                    : isAwaitingRdone || isSendingBytes
                                      ? null
                                      : <GrNext />}
                            </button>
                        </div>
                    </div>
                </footer>
            )}

        </div>
    )
}

export default PlaydynamexNew
